import {
  DataValue,
  DataValueWithKey,
  DataValueWithName,
  DatePickerFormat,
  FormatOptions,
} from '@/types/Global';
import { DataType, FormatLocaleOptions } from '@/enums/Global';
import { translationsState } from '@/plugins/localisation';
import { get, has } from 'lodash';
import { config } from '@/services/config';
import { translations } from '@/plugins/localisation/';

// Custom date formatting functions to replace date-fns
const formatDistance = (date: Date, baseDate: Date, options: any = {}) => {
  const diff = Math.abs(baseDate.getTime() - date.getTime());
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d`;
  if (hours > 0) return `${hours}h`;
  if (minutes > 0) return `${minutes}m`;
  return `${seconds}s`;
};

const formatDistanceStrict = (date: Date, baseDate: Date, options: any = {}) => {
  const diff = Math.abs(baseDate.getTime() - date.getTime());
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days} days`;
  if (hours > 0) return `${hours} hours`;
  if (minutes > 0) return `${minutes} minutes`;
  return `${seconds} seconds`;
};

export const format = (date: Date, formatStr: string) => {
  const pad = (num: number) => String(num).padStart(2, '0');
  
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());

  return formatStr
    .replace('yyyy', String(year))
    .replace('MM', month)
    .replace('dd', day)
    .replace('HH', hours)
    .replace('mm', minutes)
    .replace('ss', seconds);
};

const locales = { enGB: {} };


export const getDateFormatFromConfig = () => {
  const dateFormatFromConfig = get(config.layout, 'dates.format', {
    date: 'YYYY-MM-DD',
  } as DatePickerFormat) as DatePickerFormat;
  return dateFormatFromConfig;
};

export const getDateByQuickSelect = (monthsToAdd: number) => {
  const today = new Date();
  const tomorrow = new Date(today.setDate(today.getDate() + 1));
  return new Date(
    tomorrow.setMonth(tomorrow.getMonth() + monthsToAdd)
  ).toISOString();
};

export const formatDateTimeString = (
  timestamp: string,
  formatOptions = {} as FormatOptions,
  locale: string = config.layout.options.defaultLocale,
  type = 'short'
) => {
  const { d, t } = translations.global;
  const parsedTimestamp = Date.parse(timestamp);
  if (isNaN(parsedTimestamp) === false) {
    const date = new Date(parsedTimestamp);
    const now = new Date();

    if (formatOptions.useDistance) {
      return formatDistance(date, now, {
        addSuffix: false,
        locale: locales[locale],
      });
    }

    if (formatOptions.useDistanceStrict) {
      return formatDistanceStrict(date, now, {
        addSuffix: false,
        locale: locales[locale],
      });
    }

    if (formatOptions.useFromNow) {
      if (
        date.getDate() === now.getDate() &&
        date.getMonth() === now.getMonth() &&
        date.getFullYear() === now.getFullYear()
      ) {
        const hours = date.getHours();
        const minutes = date.getMinutes();
        let minutesStr = minutes.toString();
        if (minutes < 10) {
          minutesStr = '0' + minutesStr;
        }

        return t('date.label', {
          day: t('date.today'),
          time: hours + ':' + minutesStr,
        });
      }
    }

    if (
      formatOptions.overrideDateFormat &&
      config.layout.options.dateFormatOverride
    ) {
      return format(date, config.layout.options.dateFormatOverride);
    }

    return d(date, type, locale);
  }
  return null;
};

export const formatYesNo = (value: boolean) => {
  if (value === true) {
    return 'general.yes';
  } else {
    return 'general.no';
  }
};

export const formatDataValue = (
  dataValue: DataValue | DataValueWithKey | DataValueWithName = {
    dataType: DataType.STRING,
    dataValue: '-',
    currencyName: '',
  } as DataValue,
  formatOptions = {} as FormatOptions
) => {
  if (
    !dataValue ||
    typeof dataValue !== 'object' ||
    (dataValue && !('dataValue' in dataValue))
  ) {
    return '-';
  }

  // cannot format a boolean
  if (typeof dataValue.dataValue === 'boolean') return dataValue.dataValue;

  if (formatOptions && formatOptions.bypassFormatting) {
    return dataValue.dataValue;
  }

  const checkShouldReturnDash = (
    propertyToCheck: string | number | boolean
  ) => {
    return (
      propertyToCheck === null ||
      propertyToCheck === undefined ||
      ((propertyToCheck === 0 ||
        propertyToCheck === '0' ||
        propertyToCheck === '-0' ||
        propertyToCheck === '') &&
        !formatOptions.keepZeroAsZero)
    );
  };

  if (checkShouldReturnDash(dataValue.dataValue)) return '-';

  const locale =
    config.layout.options.defaultNumFormatLocale ||
    translationsState.locale ||
    translationsState.fallbackLocale;

  switch (dataValue.dataType) {
    case DataType.PERCENTAGE1: {
      if (typeof dataValue.dataValue === 'string') {
        dataValue.dataValue = parseFloat(dataValue.dataValue as string);
      }
      const percentDecimalPoints = formatOptions.percentDecimalPoints || 2;
      const formattedValue = dataValue.dataValue.toLocaleString(locale, {
        style: FormatLocaleOptions.PERCENT,
        maximumFractionDigits: percentDecimalPoints,
        minimumFractionDigits: percentDecimalPoints,
      }) as string;

      // check if value is -0 and return 0 instead
      const floatValue = parseFloat(formattedValue.replace(/[^0-9.-]+/g, ''));
      if (Object.is(floatValue, -0)) {
        return (0).toLocaleString(locale, {
          style: FormatLocaleOptions.PERCENT,
          maximumFractionDigits: percentDecimalPoints,
          minimumFractionDigits: percentDecimalPoints,
        });
      }
      return formattedValue;
    }
    case DataType.PERCENTAGE1_ROUNDED: {
      if (typeof dataValue.dataValue === 'string') {
        dataValue.dataValue = parseFloat(dataValue.dataValue as string);
      }
      return dataValue.dataValue.toLocaleString(locale, {
        style: FormatLocaleOptions.PERCENT,
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      }) as string;
    }
    case DataType.CURRENCY: {
      const dataVal = dataValue as DataValueWithName;

      const currency =
        formatOptions.currency || get(config.layout, 'options.currency', 'GBP');

      const currencyDecimalPoints = formatOptions.currencyDecimalPoints || 0;
      let hideCurrency = get(config.layout, 'options.hideCurrency', false);
      if (has(formatOptions, 'hideCurrencySymbol')) {
        hideCurrency = formatOptions.hideCurrencySymbol;
      }
      const style = hideCurrency
        ? FormatLocaleOptions.DECIMAL
        : FormatLocaleOptions.CURRENCY;

      const formattedOutput = dataVal.dataValue.toLocaleString(locale, {
        style: style,
        currency: currency,
        maximumFractionDigits: currencyDecimalPoints,
        minimumFractionDigits: currencyDecimalPoints,
      }) as string;

      if (checkShouldReturnDash(formattedOutput)) return '-';

      return formattedOutput;
    }
    case DataType.NUMBER: {
      if (typeof dataValue.dataValue === 'string') {
        dataValue.dataValue = parseFloat(dataValue.dataValue as string);
      }
      const numberDecimalMaxPoints =
        typeof formatOptions.numberDecimalMaxPoints === 'number'
          ? formatOptions.numberDecimalMaxPoints
          : 20;

      const formattedOutput = dataValue.dataValue.toLocaleString(locale, {
        style: FormatLocaleOptions.DECIMAL,
        maximumFractionDigits: numberDecimalMaxPoints,
        minimumFractionDigits: 0,
      }) as string;

      if (checkShouldReturnDash(formattedOutput)) return '-';

      return formattedOutput;
    }
    case DataType.STRING: {
      return dataValue.dataValue as string;
    }
    case DataType.DATETIME:
    case DataType.DATE: {
      const dateType =
        formatOptions && formatOptions.showTime ? 'long' : 'short';
      const useFromNow = formatOptions && formatOptions.useFromNow;
      const overrideDateFormat =
        formatOptions && formatOptions.overrideDateFormat;
      const formattedDate = formatDateTimeString(
        dataValue.dataValue as string,
        { useFromNow, overrideDateFormat },
        locale,
        dateType
      );
      return formattedDate as string;
    }
    default:
      return dataValue.dataValue;
  }
};

export const getFormattedValueAsString = (
  dataValue: number | string | boolean,
  dataType: DataType,
  bypassFormatting = false
) => {
  if (dataValue === '0' || dataValue === 0) return '0';
  if (!dataValue) return '';
  return formatDataValue(
    { dataType, dataValue },
    { bypassFormatting }
  ) as string;
};

export const getNumberSeparators = () => {
  const locale =
    config.layout.options.defaultNumFormatLocale ||
    translationsState.locale ||
    translationsState.fallbackLocale;
  const parts = (1234.5).toLocaleString(locale).match(/(\D+)/g);
  return {
    thousandSeparator: parts ? parts[0] : null,
    decimalSeparator: parts ? parts[1] : null,
  };
};

export const removeFormatting = (string: string) => {
  let unformatted = string;
  const numberSeparators = getNumberSeparators();
  if (numberSeparators.thousandSeparator && numberSeparators.decimalSeparator) {
    unformatted = unformatted
      .split(numberSeparators.thousandSeparator)
      .join('');
    unformatted = unformatted
      .split(numberSeparators.decimalSeparator)
      .join('.');
  }
  return unformatted.replace('%', '').trim();
};

export const formatClientSpecificValues = (clientSpecificValues: {
  [x: string]: DataValue;
}) => {
  const formattedValues: { [x: string]: string | number | boolean } = {};

  for (const [key, value] of Object.entries(clientSpecificValues)) {
    const formattedValue = formatDataValue(value as DataValue);
    formattedValues[key] = formattedValue;
  }

  return formattedValues;
};

export const formatValueByConfig = (
  metric: DataValue,
  formatOptions: FormatOptions
) => {
  const { t } = translations.global;
  const formattedValue = formatDataValue(metric);

  if (metric.dataType === DataType.DATETIME) {
    const formattedDate = formatDateTimeString(
      formattedValue as string,
      formatOptions || {}
    );
    return formatOptions.useDistanceStrict
      ? t('date.ago', { date: formattedDate })
      : formattedDate;
  } else if (
    typeof metric.dataValue === 'boolean' &&
    formatOptions.useBooleanYesNo
  ) {
    return t(formatYesNo(metric.dataValue));
  }
  return formattedValue;
};

export const formatTableValue = (
  dataValue: number,
  dataType: DataType = DataType.CURRENCY
) => {
  return formatDataValue({
    dataValue,
    dataType,
  }) as string;
};

export const formatDateWithTime = (date: DataValue) => {
  const _date = new Date(date.dataValue as string);
  return `${formatDataValue(date, { overrideDateFormat: true })} ${format(
    _date,
    'HH:mm'
  )}`;
};
